import { Router } from '@angular/router';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { LoggedUserClass } from '../models/users-class';
import { CustomerClass } from '../models/customer-class';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import { UsersService } from './users.service';
import { CacheService } from './cache.service';
import { GlobalService } from './global.service';
import { AlertService } from './alert.service';
import { SafeStorageService } from './safe-storage.service';
import { RealtimeService } from './realtime.service';
import { UserActivityService } from './user-activity.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  mensagemLogin: string;
  private refreshTimer: any;
  private crossTabSyncInProgress = false;

  // ✅ Cache para evitar JSON.parse(atob(...)) repetido quando múltiplos guards
  // chamam restoreSession() na mesma navegação (~5s de validade)
  private _cachedRestoredUser: LoggedUserClass | null = null;
  private _cacheTimestamp = 0;
  private readonly CACHE_TTL_MS = 5000;

  constructor(
    private router: Router,
    private http: HttpClient,
    private dialogRef: MatDialog,
    public globalService: GlobalService,
    public httpclienterrorService: HttpclienterrorService,
    public dialog: MatDialog,
    private usersService: UsersService,
    private cacheService: CacheService,
    private alert: AlertService,
    private storage: SafeStorageService,
    private realtimeService: RealtimeService,
    private userActivityService: UserActivityService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // Iniciar verificação de renovação automática
      this.startAutoRefreshTimer();

      // 🔒 Sincronização cross-tab: detectar logout ou troca de perfil noutra janela/aba
      this.initCrossTabSync();
    }
  }

  /**
   * Escuta alterações no localStorage feitas por OUTRAS abas/janelas do mesmo browser.
   * O evento 'storage' NUNCA dispara na aba que originou a alteração — só nas outras.
   * Protege contra:
   *  1) Logout noutra aba → força logout aqui
   *  2) Troca de utilizador noutra aba → força logout aqui (evita operar com perfil errado)
   */
  private initCrossTabSync(): void {
    window.addEventListener('storage', (event: StorageEvent) => {
      // Guard: Ignorar se já em processamento, utilizador não logado, ou já na pág. de login
      const onLoginPage = this.router.url.startsWith('/login');
      if (this.crossTabSyncInProgress || !this.globalService.loggedUserClass?.Token || onLoginPage) {
        return;
      }

      // Cenário 1: Outra aba fez logout (isLoggedin mudou para 'false')
      if (event.key === 'isLoggedin' && event.newValue === 'false') {
        console.warn('[AuthService] 🔄 Logout detectado noutra aba. A sincronizar...');
        this.handleCrossTabLogout();
        return;
      }

      // Cenário 2: Outra aba trocou de utilizador (sv_session_data mudou)
      if (event.key === this.SESSION_KEY && event.newValue) {
        try {
          const json = decodeURIComponent(escape(window.atob(event.newValue)));
          const newUser = JSON.parse(json);
          const currentUser = this.globalService.loggedUserClass;

          // Se o idUser mudou, outra aba fez login com outro utilizador (ex: Mediador entrou num cliente)
          if (currentUser && newUser.idUser && currentUser.idUser !== newUser.idUser) {
            console.warn(
              `[AuthService] 🔄 Troca de perfil detectada noutra aba (${currentUser.idUser} → ${newUser.idUser}). A sincronizar interface...`,
            );
            this.handleCrossTabUserChange();
            return;
          }

          // Se o utilizador for o mesmo, mas o token foi renovado por outra aba
          if (currentUser && currentUser.idUser === newUser.idUser && currentUser.Token !== newUser.Token) {
            console.log('[AuthService] 🔄 Token renovado noutra aba. A atualizar a sessão em memória...');
            currentUser.Token = newUser.Token;
            this._cachedRestoredUser = null; // invalidar cache

            // Atualizar os tokens nos serviços de comunicação realtime
            this.realtimeService.updateToken(newUser.Token);
            this.userActivityService.updateToken(newUser.Token);
          }
        } catch (e) {
          // Se não conseguimos ler, ignorar silenciosamente
        }
      }

      // Cenário 3: localStorage foi totalmente limpo noutra aba (key === null quando clearLocal())
      if (event.key === null) {
        console.warn('[AuthService] 🔄 localStorage limpo noutra aba. A sincronizar logout...');
        this.handleCrossTabLogout();
      }
    });
  }

  /**
   * Executa logout local quando detectada alteração de sessão noutra aba.
   * Usa flag para evitar re-entância (o logout gera múltiplos eventos storage).
   */
  private handleCrossTabLogout(): void {
    if (this.crossTabSyncInProgress) {
      return;
    }
    this.crossTabSyncInProgress = true;

    // Limpar estado em memória sem tocar novamente no localStorage
    // (para evitar loops, já que a outra aba já limpou)
    // Try-catch para evitar ObjectUnsubscribedError de Subjects RxJS já destruídos
    try {
      this.cacheService.clearCache();
      this.dialogRef.closeAll();
      this.userActivityService.onLogout();
      this.globalService.SendAdminAccessAlert = false;
      this.globalService.loggedUserClass = new LoggedUserClass();
      this.stopAutoRefreshTimer();
    } catch (e) {
      // Ignorar erros de subjects já unsubscribed durante logout forçado
    }
    window.location.href = '/login';

    // Resetar flag após um tempo seguro
    setTimeout(() => {
      this.crossTabSyncInProgress = false;
    }, 3000);
  }

  /**
   * Sincroniza a aba atual quando ocorre uma troca de perfil noutra aba.
   * Evita redirecionar para /login, pois isso limparia o localStorage.
   */
  private handleCrossTabUserChange(): void {
    if (this.crossTabSyncInProgress) {
      return;
    }
    this.crossTabSyncInProgress = true;

    try {
      this.cacheService.clearCache();
      this.dialogRef.closeAll();
      this.stopAutoRefreshTimer();
    } catch (e) {
      // Ignorar erros
    }

    // Navega para a raiz para forçar o recarregamento do novo contexto de permissões do utilizador
    window.location.href = '/';

    setTimeout(() => {
      this.crossTabSyncInProgress = false;
    }, 3000);
  }

  GetUserAuthbyNIF(vNIF: string): Observable<LoggedUserClass[]> {
    const dados = {
      NIF: vNIF,
    };

    return this.http.post<LoggedUserClass[]>(environment.URL + `/users/GetUserAuthbyNIF`, dados).pipe(
      catchError(error => {
        console.log(error);
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  GetUserId(idUser: number): Observable<LoggedUserClass> {
    const data = { idUser: idUser };
    return this.http.post<LoggedUserClass>(environment.URL + `/users/GetUserId`, data).pipe(
      catchError(error => {
        console.log(error);
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  UserAuth(vNIF: string, password: any, uuid: string): Observable<LoggedUserClass> {
    this.globalService.loadingMessage = 'A iniciar sessão...';
    const dados = {
      UUID: uuid,
      NIF: vNIF,
      Password: password,
    };

    return this.http
      .post<LoggedUserClass>(environment.URL + `/users/GetUserAuth`, dados, {
        headers: { 'X-Skip-Error': 'true' },
      })
      .pipe(
        tap((user: LoggedUserClass) => {
          //   console.log('🔑 [AuthService] UserAuth TAP: Login OK para', user?.NIF);
          if (user && !user.customerClass) {
            user.customerClass = new CustomerClass();
          }
          if (user && user.Token) {
            this.saveSession(user);
          }
        }),
        catchError(error => {
          console.log('[AuthService] Error in UserAuth:', error);
          return throwError(() => error);
        }),
      );
  }

  // =================================================================================================
  // SESSION PERSISTENCE (SECURITY: Basic Obfuscation)
  // =================================================================================================
  private readonly SESSION_KEY = 'sv_session_data';

  saveSession(user: LoggedUserClass): void {
    if (!user || !user.Token) {
      console.error(
        '❌ [AuthService] saveSession: TENTATIVA DE GRAVAR SESSÃO INVÁLIDA (User ou Token null). Abortando.',
      );
      return;
    }
    // Invalidar cache para forçar nova leitura no próximo restoreSession()
    this._cachedRestoredUser = null;
    try {
      // Simple Obfuscation to avoid plain text in LocalStorage
      // For higher security, use a library like crypto-js or rely on HttpOnly cookies (backend change)
      const sessionPayload: any = { ...(user as any) };
      delete sessionPayload.profiles;
      delete sessionPayload.selectionToken;
      delete sessionPayload.requiresProfileSelection;

      // 🛡️ NUNCA persistir blobs binários pesados de Photo na sessão do localStorage.
      // Persistir apenas metadados (PhotoPath, etc.). A imagem Photo binária é sempre
      // carregada de forma reativa a partir do backend, evitando flashes de fotos antigas
      // ao fazer refresh (F5) e quotas de storage excedidas.
      if (sessionPayload.customerClass) {
        sessionPayload.customerClass = { ...sessionPayload.customerClass };
        delete sessionPayload.customerClass.Photo;
      }
      if (sessionPayload.commercialClass) {
        sessionPayload.commercialClass = { ...sessionPayload.commercialClass };
        delete sessionPayload.commercialClass.Photo;
      }
      if (sessionPayload.accountmanagerClass) {
        sessionPayload.accountmanagerClass = { ...sessionPayload.accountmanagerClass };
        delete sessionPayload.accountmanagerClass.Photo;
      }

      const json = JSON.stringify(sessionPayload);
      const encoded = btoa(unescape(encodeURIComponent(json)));
      this.storage.setLocal(this.SESSION_KEY, encoded);
      this.storage.setLocal('isLoggedin', 'true');
      this.userActivityService.updateToken(user.Token);
      this.realtimeService.updateToken(user.Token);
    } catch (e) {
      console.error('❌ [AuthService] Error saving session', e);
    }
  }

  restoreSession(): LoggedUserClass | null {
    // ✅ Retornar cache se ainda válido (cobre múltiplas chamadas na mesma navegação)
    if (this._cachedRestoredUser && Date.now() - this._cacheTimestamp < this.CACHE_TTL_MS) {
      return this._cachedRestoredUser;
    }

    try {
      const encoded = this.storage.getLocal(this.SESSION_KEY);
      if (encoded) {
        const json = decodeURIComponent(escape(window.atob(encoded)));
        const data: any = JSON.parse(json);

        // Basic validation
        if (data && data.Token) {
          // Check if token is already expired
          const secondsLeft = this.getTokenSecondsRemaining(data.Token);
          if (secondsLeft <= 0) {
            console.warn('⚠️ [AuthService] Sessão restaurada ignorada: Token JWT expirado.');
            this.storage.removeLocal(this.SESSION_KEY);
            return null;
          }

          // Reconstruir instâncias para manter métodos (ex: parseDates)
          const user = Object.assign(new LoggedUserClass(), data);
          if (data.customerClass) {
            user.customerClass = Object.assign(new CustomerClass(), data.customerClass);
          } else {
            user.customerClass = new CustomerClass();
          }

          // 🛡️ Blindagem contra sessões legadas em cache que ainda guardem Photo binária:
          // Limpar qualquer foto binária restaurada da cache local para evitar o flash de imagem desatualizada no F5
          if (user.customerClass) {
            user.customerClass.Photo = null;
          }
          if (user.commercialClass) {
            user.commercialClass.Photo = null;
          }
          if (user.accountmanagerClass) {
            user.accountmanagerClass.Photo = null;
          }

          // Garantir sincronização de origIdUser / origNIF / origName a partir do JWT
          try {
            const parts = String(user.Token || '').split('.');
            if (parts.length === 3) {
              let payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
              while (payloadB64.length % 4) payloadB64 += '=';
              const dec = JSON.parse(window.atob(payloadB64));
              if (dec) {
                if (dec.origIdUser && !user.origIdUser) user.origIdUser = Number(dec.origIdUser);
                if (dec.origNIF && !user.origNIF) user.origNIF = String(dec.origNIF).trim();
                if (dec.origName && !user.origName) user.origName = String(dec.origName).trim();
                if (dec.origIdCustomer && !user.origIdCustomer) user.origIdCustomer = Number(dec.origIdCustomer);
                if (dec.Admin !== undefined) user.Admin = !!dec.Admin;
                if (dec.SiteManagement !== undefined) user.SiteManagement = !!dec.SiteManagement;
                if (dec.ClientArea !== undefined) user.ClientArea = !!dec.ClientArea;
                if (dec.CommercialArea !== undefined) user.CommercialArea = !!dec.CommercialArea;
                if (dec.IntermediaryArea !== undefined) user.IntermediaryArea = !!dec.IntermediaryArea;
                if (!user.NavigateTo && dec.NavigateTo) user.NavigateTo = dec.NavigateTo;
              }
            }
          } catch (_) {}

          // Guardar em cache
          this._cachedRestoredUser = user;
          this._cacheTimestamp = Date.now();
          return user;
        } else {
          console.warn('⚠️ [AuthService] Sessão restaurada inválida (sem token).');
        }
      } else {
        console.warn(
          '⚠️ [AuthService] restoreSession: Nenhuma sessão encontrada no localStorage (Key: ' + this.SESSION_KEY + ').',
        );
      }
    } catch (e) {
      console.error('❌ [AuthService] Erro crítico ao restaurar sessão (dados corrompidos?):', e);
      this._cachedRestoredUser = null;
      return null;
    }
    return null;
  }

  maintenance() {
    this.router.navigate(['/maintenance'], { skipLocationChange: false });
    this.globalService.loggedUserClass = new LoggedUserClass();
  }

  logout(notify: boolean = true, message: string = 'A terminar sessão...', redirect: boolean = true) {
    // Invalidar cache do restoreSession()
    this._cachedRestoredUser = null;

    if (notify) {
      this.globalService.loadingSpinner = true;
      this.globalService.loadingMessage = message;
    }

    // Limpar cookies
    if (typeof document !== 'undefined') {
      var cookies = document.cookie.split(';');
      for (var i = 0; i < cookies.length; i++) {
        var cookie = cookies[i];
        var eqPos = cookie.indexOf('=');
        var name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      }
    }

    // Limpar sessionStorage
    this.storage.clearSession();

    // Preservar preferências do utilizador antes de limpar localStorage
    const cookieConsent = this.storage.getLocal('cookieConsent');
    const cookieConsentTime = this.storage.getLocal('cookieConsentTime');
    const tourCompleted = this.storage.getLocal('clientAreaTourCompleted');

    // Limpar localStorage
    this.storage.clearLocal();

    // Restaurar preferências do utilizador
    if (cookieConsent) this.storage.setLocal('cookieConsent', cookieConsent);
    if (cookieConsentTime) this.storage.setLocal('cookieConsentTime', cookieConsentTime);
    if (tourCompleted) this.storage.setLocal('clientAreaTourCompleted', tourCompleted);

    // this.googleAnalyticsService.sendEmitter('AuthService', 'logout', 'Termina a sessão', 0,);
    // Limpar o cache do HttpClient
    this.cacheService.clearCache(); // Limpar o cache do HttpClient
    // document.location.reload();
    this.storage.setLocal('isLoggedin', 'false');
    this.dialogRef.closeAll();
    this.userActivityService.onLogout();

    this.globalService.SendAdminAccessAlert = false;
    this.globalService.insuranceclaimsClass = null as any;
    this.clearClaimDraftsIndexedDb();
    this.globalService.loggedUserClass = new LoggedUserClass();

    if (redirect) {
      this.router.navigate(['/home'], { skipLocationChange: false }).then(() => {
        this.globalService.loadingSpinner = false;
      });
    } else {
      this.globalService.loadingSpinner = false;
    }
    this.stopAutoRefreshTimer();
  }

  logoutToHome() {
    this._cachedRestoredUser = null;
    this.cacheService.clearCache(); // Limpar o cache do HttpClient
    this.dialogRef.closeAll();
    this.userActivityService.onLogout();
    this.storage.removeLocal(this.SESSION_KEY);
    this.storage.setLocal('isLoggedin', 'false');
    this.globalService.insuranceclaimsClass = null as any;
    this.clearClaimDraftsIndexedDb();
    this.globalService.loggedUserClass = new LoggedUserClass();

    this.router.navigate(['/home'], { skipLocationChange: false });
    this.stopAutoRefreshTimer();
  }

  private clearClaimDraftsIndexedDb(): void {
    try {
      if (typeof indexedDB !== 'undefined') {
        indexedDB.deleteDatabase('SV_ClaimDraftsDB');
      }
    } catch (_) {}
  }

  isLoggedin(loggedUserClass: LoggedUserClass): boolean {
    if (loggedUserClass === undefined || loggedUserClass === null) {
      //   console.warn('[AuthService.isLoggedin] ❌ False (User is undefined/null)');
      return false;
    }
    const hasToken = loggedUserClass?.Token !== '' && loggedUserClass?.Token !== undefined;
    // if (!hasToken) {
    //   console.warn('[AuthService.isLoggedin] ❌ False (User exists but NO TOKEN)', loggedUserClass);
    // }
    return hasToken;
  }

  createUser(newUserClass: LoggedUserClass) {
    return new Promise<LoggedUserClass>(resolve => {
      this.usersService.UsersNew(newUserClass, this.globalService.loggedUserClass?.UUID || '').subscribe({
        next: dados => {
          console.log(dados);
        },
        error: error => {
          this.alert.showRequestFailed();
          console.log(error);
          resolve(null);
        },
        complete: () => {
          this.GetUserAuthbyNIF(newUserClass.NIF).subscribe({
            next: dados => (this.globalService.loggedUserClass = dados[0]),
            error: error => {
              this.alert.showRequestFailed();
              console.log(error);
              resolve(null);
            },
            complete: async () => {
              resolve(this.globalService.loggedUserClass);
            },
          });
        },
      });
    });
  }
  notifyAdminAccess(user: any): Observable<any> {
    return this.http.post(environment.URL + '/admin/notify-access', { user });
  }

  /**
   * Renova o token JWT solicitando um novo token ao backend
   */
  refreshToken(): Observable<LoggedUserClass> {
    const currentUser = this.globalService.loggedUserClass;

    if (!currentUser || !currentUser.Token) {
      return throwError(() => 'Nenhum utilizador autenticado');
    }

    return this.http
      .post<LoggedUserClass>(environment.URL + '/users/refresh-token', { idUser: currentUser.idUser })
      .pipe(
        tap((updatedUser: LoggedUserClass) => {
          if (updatedUser && updatedUser.Token) {
            // Atualizar token mantendo outros dados do utilizador
            currentUser.Token = updatedUser.Token;
            this.saveSession(currentUser);
            this.globalService.loggedUserClass = currentUser;
            // console.log('[AuthService] Token renovado com sucesso');
          }
        }),
        catchError(error => {
          console.error('[AuthService] Erro ao renovar token:', error);
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  /**
   * Inicia o timer para renovação automática do token.
   * Verifica a cada 10 minutos o tempo real restante no JWT
   * e renova quando faltam menos de 60 minutos.
   * Protege contra expiração após F5/reload da página.
   */
  private startAutoRefreshTimer() {
    this.stopAutoRefreshTimer();

    // Verificar a cada 10 minutos o tempo real restante do token
    this.refreshTimer = setInterval(
      () => {
        const user = this.globalService.loggedUserClass;
        if (user && user.Token) {
          // Decodificar o JWT para verificar o tempo real restante
          const secondsLeft = this.getTokenSecondsRemaining(user.Token);

          // Renovar se faltam menos de 60 minutos (3600 segundos)
          if (secondsLeft > 0 && secondsLeft < 3600) {
            console.log(
              `[AuthService] Token com ${Math.floor(secondsLeft / 60)} min restantes — a renovar automaticamente`,
            );
            this.refreshToken().subscribe({
              next: updatedUser => {
                if (updatedUser && updatedUser.Token) {
                  // Notificar os serviços de Socket para usarem o novo token
                  this.realtimeService.updateToken(updatedUser.Token);
                  this.userActivityService.updateToken(updatedUser.Token);
                }
              },
            });
          }
        }
      },
      10 * 60 * 1000, // Verificar a cada 10 minutos
    );
  }

  /**
   * Obtém os segundos restantes até a expiração do token JWT
   */
  private getTokenSecondsRemaining(token: string): number {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return 0;
      let payload = parts[1];
      payload = payload.replace(/-/g, '+').replace(/_/g, '/');
      while (payload.length % 4) {
        payload += '=';
      }
      const decoded = JSON.parse(atob(payload));
      if (!decoded.exp) return 0;
      return decoded.exp - Math.floor(Date.now() / 1000);
    } catch {
      return 0;
    }
  }

  private stopAutoRefreshTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // =================================================================================================
  // GOOGLE IDENTITY SERVICES (OAUTH 2.0 / OIDC)
  // =================================================================================================
  private _googleClientId: string | null = null;
  private _gisScriptPromise: Promise<void> | null = null;

  /**
   * Obtém o Google Client ID do environment local ou da API (/users/GoogleConfig).
   */
  async resolveGoogleClientId(): Promise<string> {
    if (this._googleClientId) {
      return this._googleClientId;
    }
    const envClientId = String((environment as any).googleClientId || '').trim();
    if (envClientId) {
      this._googleClientId = envClientId;
      return envClientId;
    }
    try {
      const res: any = await this.http
        .get(`${environment.URL}/users/GoogleConfig`, {
          headers: { 'X-Skip-Error': 'true' },
        })
        .toPromise();
      if (res && res.clientId) {
        this._googleClientId = String(res.clientId).trim();
        return this._googleClientId;
      }
    } catch {
      // Ignorar falha silenciosamente
    }
    return '';
  }

  /**
   * Carrega o SDK oficial Google Identity Services (https://accounts.google.com/gsi/client) sob demanda.
   */
  loadGoogleIdentityScript(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.reject(new Error('Google Identity só está disponível no browser.'));
    }
    if ((window as any).google?.accounts?.oauth2) {
      return Promise.resolve();
    }
    if (this._gisScriptPromise) {
      return this._gisScriptPromise;
    }

    this._gisScriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existing) {
        existing.remove();
      }

      let settled = false;
      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        this._gisScriptPromise = null;
        reject(
          new Error('Não foi possível ligar ao serviço Google neste momento. Pode entrar com NIF e palavra-passe.'),
        );
      }, 8000);

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve();
      };
      script.onerror = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        this._gisScriptPromise = null;
        script.remove();
        reject(
          new Error(
            'Não foi possível carregar a autenticação Google neste browser. Pode entrar normalmente com NIF e palavra-passe.',
          ),
        );
      };
      document.head.appendChild(script);
    });

    return this._gisScriptPromise;
  }

  /**
   * Abre o seletor oficial de conta Google (OAuth 2.0 Popup) e devolve o access_token verificado.
   */
  async requestGoogleAccessToken(): Promise<string> {
    const clientId = await this.resolveGoogleClientId();
    if (!clientId) {
      throw new Error(
        'A autenticação Google ainda não tem o GOOGLE_CLIENT_ID configurado no servidor (.env) ou no environment.',
      );
    }

    await this.loadGoogleIdentityScript();

    return new Promise<string>((resolve, reject) => {
      try {
        const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid email profile',
          prompt: 'select_account',
          callback: (response: any) => {
            if (response?.error) {
              reject(new Error(response.error_description || 'Autenticação Google cancelada.'));
              return;
            }
            if (!response?.access_token) {
              reject(new Error('Não foi recebido token da Google.'));
              return;
            }
            resolve(response.access_token);
          },
          error_callback: (err: any) => {
            if (err?.type === 'popup_closed') {
              reject(new Error('POPUP_CLOSED'));
              return;
            }
            reject(new Error('Não foi possível abrir a janela de autenticação da Google.'));
          },
        });

        tokenClient.requestAccessToken();
      } catch (e: any) {
        reject(new Error(e?.message || 'Erro ao inicializar autenticação Google.'));
      }
    });
  }

  /**
   * Autentica o utilizador via Google (ou seleciona o perfil NIF quando existem múltiplos NIFs associados).
   */
  GoogleAuth(payload: {
    accessToken?: string;
    credential?: string;
    NIF?: string;
    strictNif?: boolean;
    selectionToken?: string;
    idUser?: number;
  }): Observable<any> {
    this.globalService.loadingMessage = 'A validar conta Google...';
    return this.http
      .post<any>(environment.URL + `/users/GoogleAuth`, payload, {
        headers: { 'X-Skip-Error': 'true' },
      })
      .pipe(
        tap((resp: any) => {
          if (resp && !resp.customerClass) {
            resp.customerClass = new CustomerClass();
          }
          if (resp && resp.Auth && resp.Token) {
            this.saveSession(resp);
          }
        }),
        catchError(error => {
          return throwError(() => error);
        }),
      );
  }

  /**
   * Consulta o estado de associação Google da conta autenticada atual.
   */
  GetGoogleStatus(): Observable<{
    success: boolean;
    linked: boolean;
    googleEmail: string;
    googleLinkedAt?: string | null;
    linkedAt?: string | null;
    pictureDataUrl?: string | null;
    linkedProfiles?: { idUser: number; NIF: string; Name: string; isDirectLink: boolean }[];
    schemaPending?: boolean;
  }> {
    return this.http.post<any>(environment.URL + `/users/GoogleStatus`, {}, { headers: { 'X-Skip-Error': 'true' } });
  }

  /**
   * Associa explicitamente uma conta Google à sessão autenticada atual.
   */
  LinkGoogleAccount(
    accessTokenOrPayload: string | { accessToken: string; linkAuthorizedNifs?: boolean; forceApplyPhoto?: boolean },
    linkAuthorizedNifs = true,
  ): Observable<{
    success: boolean;
    linked: boolean;
    googleEmail: string;
    googleName?: string;
    pictureDataUrl?: string | null;
    savedPhotoPath?: string | null;
    linkedProfiles?: { idUser: number; NIF: string; Name: string; isDirectLink: boolean }[];
  }> {
    const body =
      typeof accessTokenOrPayload === 'string'
        ? { accessToken: accessTokenOrPayload, linkAuthorizedNifs }
        : {
            accessToken: accessTokenOrPayload.accessToken,
            linkAuthorizedNifs: accessTokenOrPayload.linkAuthorizedNifs ?? true,
            forceApplyPhoto: !!accessTokenOrPayload.forceApplyPhoto,
          };
    return this.http.post<any>(environment.URL + `/users/GoogleLink`, body, {
      headers: { 'X-Skip-Error': 'true' },
    });
  }

  /**
   * Desassocia a conta Google da sessão autenticada atual.
   */
  UnlinkGoogleAccount(unlinkAll = false): Observable<{ success: boolean; linked: boolean }> {
    return this.http.post<any>(
      environment.URL + `/users/GoogleUnlink`,
      { unlinkAll },
      { headers: { 'X-Skip-Error': 'true' } },
    );
  }
}
